/* eslint camelcase: 0 */
import { Intersection } from 'js-2dmath';

const add = (p0, p1) => {
  return {
    x: p0.x + p1.x,
    y: p0.y + p1.y
  };
};

const subtract = (p0, p1) => {
  return {
    x: p0.x - p1.x,
    y: p0.y - p1.y,
  };
};

const scale = (point, c) => {
  return {
    x: point.x * c,
    y: point.y * c
  };
};

const midpoint = (p0, p1) => {
  return {
    x: (p0.x + p1.x)/2,
    y: (p0.y + p1.y)/2,
  };
};

// length-1 vector
const unitVector = theta => {
  return {
    x: Math.cos(theta),
    y: Math.sin(theta),
  };
};

const sqrNorm = ({ x, y }) => x * x + y * y;

export const pointDistance = (p0, p1) => {
  return Math.sqrt(sqrNorm(subtract(p0, p1)));
};

export const normalizeAngle = theta => {
  while (theta > 2 * Math.PI) {
    theta -= 2 * Math.PI;
  }
  while (theta < 0) {
    theta += 2 * Math.PI;
  }

  return theta;
};

// Maps cb over array, then takes the average of the resulting array.
// Special case of foldMap for the Additive monoid of numbers ;)
export const averageMap = (array, cb) => {
  if (array.length === 0) {
    return 0;
  } else {
    return array.map(cb).reduce((a, b) => (a + b), 0) / array.length;
  }
};


const _getLineIntersection = (p0, p1, p2, p3) => {
  const s1 = subtract(p1, p0);
  const s2 = subtract(p3, p2);

  const s = (-s1.y * (p0.x - p2.x) + s1.x * (p0.y - p2.y)) /
      (-s2.x * s1.y + s1.x * s2.y);

  const t = (s2.x * (p0.y - p2.y) - s2.y * (p0.x - p2.x)) /
      (-s2.x * s1.y + s1.x * s2.y);

  return { s, t, s1, s2 };
};



export const getLineIntersection = (p0, p1, p2, p3) => {
  const { s, t, s1, s2 } = _getLineIntersection(p0, p1, p2, p3);

  if (!isFinite(s) || !isFinite(t)) return null;

  return add(scale(s1, t), p0);
};

export const getLineSegmentIntersection = (p0, p1, p2, p3) => {
  //p0, p1 are the first segment, p2, p3 are the second line
  //returns null if there is no intersection
  const { s, t, s1, s2 } = _getLineIntersection(p0, p1, p2, p3);

  if (t > 1 || t < 0 || s > 1 || s < 0 ) return null;

  return add(scale(s1, t), p0);
};

export const getLineSegmentRectIntersections = (p0, p1, rect) => {
  const topLeft = { x: rect.left, y: rect.top };
  const topRight = { x: rect.right, y: rect.top };
  const bottomRight = { x: rect.right, y: rect.bottom };
  const bottomLeft = { x: rect.left, y: rect.bottom };
  const intersections = [];
  intersections.push(getLineSegmentIntersection(p0, p1, topLeft, topRight));
  intersections.push(getLineSegmentIntersection(p0, p1, topRight, bottomRight));
  intersections.push(getLineSegmentIntersection(p0, p1, bottomRight, bottomLeft));
  intersections.push(getLineSegmentIntersection(p0, p1, bottomLeft, topLeft));

  if (intersections.filter(i => i).length)
    return intersections
      .filter(i => i)
      .filter(i => Number.isFinite(i.x) && Number.isFinite(i.y));

  return false;
};

// returns an array of real solutions to the quadratic ax^2 + bx + c = 0
const quadratic = (a, b, c) => {
  if (a === 0) return [ -c/b ];
  const discriminant = b*b - 4*a*c;
  if (discriminant < 0) {
    return [];
  } else {
    const y = - b / (2 * a);
    const x = Math.sqrt(discriminant) / (2 * a);
    return [y + x, y - x];
  }
};


export const angleDiff = (a, b, sweepFlag) => {
  if (sweepFlag) {
    const tmp = a;
    a = b;
    b = tmp;
  }
  let d = ((a - b + 3 * Math.PI) % (2 * Math.PI)) - Math.PI;
  if (d < 0) d += Math.PI * 2;
  if (d > Math.PI * 2) d -= Math.PI * 2;
  return d;
};

// checks if the arc passing through p1, p2, p3,
// intersects the line segment from p4 to p5
const arcSegmentIntersectionPoints = (p1, p2, p3, circleIntersection, sweepFlag) => (p4, p5) => {

  const [center, r] = findCircleFromPoints(p1, p2, p3);

  const fromPoint = p1;
  const thirdPoint = p2;
  const toPoint = p3;

  const fromPointAngle = computeSlope(center, fromPoint);
  const toPointAngle = computeSlope(center, toPoint);


  const intersectionResult = Intersection.$circle_segment2(center.x, center.y, r, p4.x, p4.y, p5.x, p5.y, true, true);

  if (intersectionResult && intersectionResult.points) {
    const points = intersectionResult.points.map(point => {
      return { x: point[0], y: point[1] };
    });

    if (circleIntersection) {
      return points;
    } else {
      return points.filter(point => {
        const newPointAngle = computeSlope(center, point);
        const fromNewDiff = angleDiff(fromPointAngle, newPointAngle, sweepFlag);
        const fromToDiff = angleDiff(fromPointAngle, toPointAngle, sweepFlag);

        return fromNewDiff < fromToDiff;
      });
    }

  } else {
    return [];
  }
};

export const checkLinkRectIntersection = ({ link, rect, linkTag }) => {
  if (link.lineCurvature === 'elbow') {
    if (linkTag && linkTag.pathArray) {
      return pathRectIntersection({ path: linkTag.pathArray, rect });
    } else {
      return false;
    }
  } else {

    const { fromEdgePoint: fromPoint, thirdPoint, toEdgePoint: toPoint, sweepFlag } = linkTag;

    return checkArcRectangleIntersection(fromPoint, thirdPoint, toPoint, {x: rect.left, y: rect.top}, {x: rect.right, y: rect.bottom }, sweepFlag);
  }
};

export const pathRectIntersection = ({ path, rect }) => {
  if (path.length > 1) {
    for (let i = 0; i < path.length - 1; i++) {
      const intersections = getLineSegmentRectIntersections(path[i], path[i + 1], rect);
      if (intersections) return intersections;
    }
  }
  return false;
};

export const arcRectangleIntersectionPoints = (p1, p2, p3, topLeft, bottomRight, circleIntersection, sweepFlag) => {
  const topRight = {
    x: bottomRight.x,
    y: topLeft.y
  };

  const bottomLeft = {
    x: topLeft.x,
    y: bottomRight.y,
  };

  const segmentIntersections = arcSegmentIntersectionPoints(p1, p2, p3, circleIntersection, sweepFlag);

  return segmentIntersections(topLeft, topRight)
    .concat(segmentIntersections(topRight, bottomRight))
    .concat(segmentIntersections(bottomRight, bottomLeft))
    .concat(segmentIntersections(bottomLeft, topLeft));
};

export const checkArcRectangleIntersection = (p1, p2, p3, topLeft, bottomRight, sweepFlag) =>
  (arcRectangleIntersectionPoints(p1, p2, p3, topLeft, bottomRight, false, sweepFlag).length > 0);

// should we rewrite this synthetically? i.e. get the perpendicular bisectors of bc and cd, then find their intersection?
export const findCircleFromPoints = (b, c, d) => {

  const temp = sqrNorm(c);
  const bc = (sqrNorm(b) - temp) / 2.0;
  const cd = (temp - sqrNorm(d)) / 2.0;

  const bToC = subtract(b, c);
  const cToD = subtract(c, d);
  const det = bToC.x * cToD.y - cToD.x * bToC.y;

  const center = {
    x: (bc * cToD.y - cd * bToC.y) / det,
    y: (bToC.x * cd - cToD.x * bc) / det,
  };
  const radius = Math.sqrt(sqrNorm(subtract(center, b)));

  return [center, radius];
};

export const computeSlope = (pointA, pointB) => {
  const diff = subtract(pointB, pointA);
  const slope = Math.atan2(diff.y, diff.x);
  return slope;
};

export const getAnglesFromPoints = (pointA, pointB, pointC) => {
  const angleA = (computeSlope(pointA, pointC)) - computeSlope(pointA, pointB);
  const angleB = (computeSlope(pointB, pointC)) - computeSlope(pointB, pointA);
  return [angleA, angleB];
};

export const getPointFromAngles = (pointA, angleA, pointB, angleB) => {
  const newAngleA = computeSlope(pointA, pointB) + angleA;
  const point1 = add(pointA, scale(unitVector(newAngleA), 40));

  const newAngleB = computeSlope(pointB, pointA) + angleB;
  const point2 = add(pointB, scale(unitVector(newAngleB), 40));

  let point = getLineIntersection(
    pointA,
    point1,
    pointB,
    point2
  );

  if (point === null) {
    // if lines don't intersect just choose the midpoint
    point = midpoint(pointA, pointB);
  }

  return point;
};

export const findRelDragStartPoint = ({ fromNodeRect, toPoint }) => {

  const fromCenterPoint = { x: fromNodeRect.left + fromNodeRect.width / 2, y: fromNodeRect.top + 28 };

  const topLeft = { x: fromNodeRect.left, y: fromNodeRect.top };
  const topRight = { x: fromNodeRect.right, y: fromNodeRect.top };
  const bottomLeft = { x: fromNodeRect.left, y: fromNodeRect.bottom };
  const bottomRight = { x: fromNodeRect.right, y: fromNodeRect.bottom };

  const result = getLineSegmentIntersection(topLeft, topRight, fromCenterPoint, toPoint)
    || getLineSegmentIntersection(topRight, bottomRight, fromCenterPoint, toPoint)
    || getLineSegmentIntersection(bottomRight, bottomLeft, fromCenterPoint, toPoint)
    || getLineSegmentIntersection(bottomLeft, topLeft, fromCenterPoint, toPoint)
    || topLeft;
  return result;

};

export const movePointTowardsPoint = ({ subject, object, magnitude }) => {
  const angle = computeSlope(subject, object);

  return {
    x: subject.x + magnitude * Math.cos(angle),
    y: subject.y + magnitude * Math.sin(angle),
  };
};
export const point2Rect = point => {
  return {
    top: point.y,
    bottom: point.y,
    left: point.x,
    right: point.x,
  };
};

export const expandRect = ({ rect, margin }) => {
  return {
    top: rect.top - margin,
    bottom: rect.bottom + margin,
    left: rect.left - margin,
    right: rect.right + margin,
  };
};

export const rectIsFullyWithinRect = (rectA, rectB) => {
  return rectA.top > rectB.top
    && rectA.bottom < rectB.bottom
    && rectA.left > rectB.left
    && rectA.right < rectB.right;
};

export const lineSegmentFullyWithinRect = (p0, p1, rect) => {
  return rect.top < Math.min(p0.y, p1.y)
    && rect.bottom > Math.max(p0.y, p1.y)
    && rect.left < Math.min(p0.x, p1.x)
    && rect.right > Math.max(p0.x, p1.x);
};




// WEBPACK FOOTER //
// ./starling/src/lib/geom.js